import React, { useCallback } from 'react'
import { Platform, Pressable } from 'react-native'
import { useTranslation } from 'react-i18next'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import TileBlur from '@ui/tiles/TileBlur'
import Button from '@ui/buttons/Button'
import useStartOrder from '@order/hooks/useStartOrder'
import { useActiveDeal } from '@order/hooks/useDeal'
import { TileGlassProps } from '@ui/tiles/TileGlass/types'

interface Props {
  size?: 'small' | 'large'
}

export default function OrderSection({ size = 'small' }: Props) {
  const { animation, onPressIn, onPressOut } = useAnimatedPress()
  const { t } = useTranslation(['customer'])
  const text = `customer:home.sections.order.${size}`

  const startOrder = useStartOrder()

  const deal = useActiveDeal()

  const onPress = useCallback(() => {
    startOrder(deal?.settings?.flow)
  }, [startOrder, deal])

  const glassProps: Partial<TileGlassProps> =
    size === 'large'
      ? {
          animation,
          image: require('@/images/orders.png'),
          imageStyle: {
            opacity: 1,
            transform: Platform.select({
              default: [{ translateX: -25 }, { translateY: 75 }],
              web: [{ translateX: -50 }, { translateY: 75 }],
            }),
            transformOrigin: 'bottom right',
          },
          blur: Platform.select({ default: 100, web: 50 }),
          blurStyle: Platform.select({
            default: {
              opacity: 0.75,
              transform: [
                { translateX: 200 },
                { translateY: 325 },
                { scale: 1.75 },
                { rotate: '-15deg' },
              ],
            },
            web: {
              opacity: 1,
              transform: [
                { translateX: 220 },
                { translateY: 350 },
                { scale: 1.75 },
                { rotate: '-15deg' },
              ],
            },
          }),
        }
      : {
          animation,
          image: require('@/images/orders.png'),
          imageWrapperStyle: {
            transform: [{ scale: Platform.select({ default: 0.8, web: 1 }) }],
          },
          imageStyle: {
            opacity: 1,
            transform: Platform.select({
              default: [{ translateX: 160 }, { translateY: 100 }],
              web: [{ translateX: 50 }, { translateY: 125 }],
            }),
            transformOrigin: 'bottom right',
          },
          blur: Platform.select({ default: 100, web: 50 }),
          blurStyle: Platform.select({
            default: {
              opacity: 0.75,
              transform: [
                { translateX: 200 },
                { translateY: 325 },
                { scaleX: 1.75 },
                { scaleY: 1.75 },
                { rotate: '-15deg' },
              ],
            },
            web: {
              opacity: 0.75,
              transform: [
                { translateX: 100 },
                { translateY: 200 },
                { scale: 1 },
              ],
            },
          }),
        }

  return (
    <Pressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      className="flex-1"
    >
      <TileBlur
        variant="small"
        title={t(`${text}.title`)}
        desc={t(`${text}.desc`)}
        cta={
          <Button
            noninteractive
            animation={animation}
            onPress={onPress}
            size="sm"
            variant="outlined"
            label={t(`${text}.cta`)}
          />
        }
        classNameDesc={
          size === 'large'
            ? Platform.select({ default: 'pr-8', web: 'pr-24' })
            : undefined
        }
        glassProps={glassProps}
      />
    </Pressable>
  )
}
